import React, {useEffect, useCallback, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  ButtonWithIcon,
  CardSkeletonLoader,
  ErrorBoundary
} from '@/components/atoms';
import {SaveCancelFooter, SectionTitle} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';
import {handleGetActivity} from '@/services/activities';
import {useAppSelector, useAppDispatch} from '@/store';
// import { WARNING_MESSAGE } from '@/types/Activity'

import {setToastNotifications} from '@/store/slices/commonSlice';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {getFormattedDate, getCurrencySymbol} from '@/utils/commons';
import {COLOR} from '@/utils/constants/colors';
import {Constants, localCurrencyText} from '@/utils/constants/commonConstants';
import {SIZE} from '@/utils/constants/globalStyle';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {validate} from 'uuid';
import {useErrorHandler} from '@/hooks/useErrorHandler';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {setActivityFormData} from '@/store/slices/activitySlice';
import {getModal} from '@/utils/CommonModal';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  ACTIVITY_TYPE,
  isVideoActivityType,
  isFreeKickActivityType,
  isWheelOfFortuneActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {logger} from '@/helpers/logger';
import {
  isActivityReviewEditable,
  isActivitySaveDraftEnabled
} from '@/helpers/activityUtils';
import './ActivityReview.scss';
import SurveyActivityReview from '@/containers/Activity/ActivityReview/SurveyActivityReview';
import VideoActivityReview from '@/containers/Activity/ActivityReview/VideoActivityReview';
import FreeKickGameReview from '@/containers/Activity/ActivityReview/FreeKickGameReview';
import WheelOfFortuneGameReview from '@/containers/Activity/ActivityReview/WheelOfFortuneGameReview';
import BackgammonGameReview from '@/containers/Activity/ActivityReview/BackgammonGameReview';
import EasyOrderActivityReview from '@/containers/Activity/ActivityReview/EasyOrderActivityReview';
import {formatTagsCommaSeparated} from '@/utils/activityReviewUtils';

const getExcludedKeys = (activityType) => {
  const baseExcludedKeys = ['status', 'name', 'uuid', 'id', 'allCustomers'];

  // For Currency to Points, show hasEndDate field
  if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
    return baseExcludedKeys;
  }

  // For other activities, exclude hasEndDate
  return [...baseExcludedKeys, 'hasEndDate'];
};

const SURVEY_HIDDEN_KEYS = ['segmentExclude', 'excludedSegments'];

const normalizeUrl = (url) => {
  if (!url || String(url).trim() === '') return '';
  return String(url).trim();
};

const ActivityReviewField = ({label, value}) => {
  const countryCode = useAppSelector(selectCountryCode);
  const displayCurrency = useMemo(
    () => getCurrencySymbol(countryCode),
    [countryCode]
  );

  let displayValue = value;
  const upperCaseLabels = [
    'Activity Type',
    'Receive Points',
    'Order Type',
    'Activity Name'
  ];
  if (upperCaseLabels.includes(label) && typeof value === 'string') {
    displayValue = value.toUpperCase();
  }
  // Replace colons with underscores in Activity Name
  if (label === 'Activity Name' && typeof displayValue === 'string') {
    displayValue = displayValue.replace(/:/g, '_');
  }
  if (label === 'Start Date' || label === 'End Date') {
    displayValue = getFormattedDate(value, false, '-', 'yyyy-mm-dd');
  }
  // Format hasEndDate as YES/NO
  if (label === 'Has End Date') {
    displayValue = value ? 'YES' : 'NO';
  }
  // Currenncy formatting
  if (label === 'Local Currency' && displayValue) {
    const symbol = String(displayCurrency);
    const valueStr = String(displayValue);
    if (!valueStr.includes(symbol)) {
      displayValue = `${valueStr} ${symbol}`;
    }
  }
  // Review display: segment rows may be grouped ids or chip objects—format human-readable labels
  if (Array.isArray(displayValue)) {
    const segmentLikeLabels = [
      'Segments',
      'Selected Segments',
      'Excluded Segments'
    ];
    if (segmentLikeLabels.includes(label)) {
      displayValue = formatTagsCommaSeparated(displayValue, {
        preserveGroups: true,
        emptyFallback: ''
      });
    } else {
      displayValue = displayValue.join(', ');
    }
  }
  // Format noOfDays as "X Days" for Point Expiration activity
  if (label === 'No Of Days' || label === 'Frequency') {
    displayValue = `${value} Days`;
  }
  if (
    displayValue === null ||
    displayValue === undefined ||
    displayValue === ''
  ) {
    displayValue = '-';
  }

  return (
    <>
      {label === 'Start Date' && <SectionTitle label={'Availability'} />}
      {(label === 'Local Currency' || label === 'Points To Redeem') && (
        <SectionTitle label={'Point Allocation'} />
      )}
      {label === 'Number Of Cases' && (
        <SectionTitle label={'Point Allocation (Number Of Cases)'} />
      )}
      <div className='mt-4 flex w-full border-b border-gray-700 pb-2 md:w-1/2'>
        <div className='w-1/2'>
          <p className='pl-1 text-sm text-[#D0D0D0]'>
            {label === 'Receive Points' ? 'Frequency' : label}
          </p>
        </div>
        <div className='w-1/2'>
          <p className='pl-1 font-bold text-white'>{displayValue}</p>
        </div>
      </div>
    </>
  );
};

ActivityReviewField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object
  ])
};

const ActivityReview = ({cancelModalHandlers, isReviewViewMode = false}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {handleApiError} = useErrorHandler();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const isReviewPublished =
    reviewParam?.toLowerCase() === Constants.ReviewPublished.toLowerCase();
  const isReviewCompleted =
    reviewParam?.toLowerCase() === Constants.ReviewCompleted.toLowerCase();
  const isReviewInProgress =
    reviewParam?.toLowerCase() === Constants.InProgress.toLowerCase();

  const [loading, setLoading] = useState(false);
  const {
    getFormDataWithOrder,
    handlePublish,
    handleDraft,
    handleCancel: hookHandleCancel,
    activityFormData,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
    isNameValid,
    handleValidationModalClose
  } = useActivityReview();

  const getActivity = useCallback(
    async (id) => {
      try {
        const paramKey = validate(id) ? 'uuid' : 'id';
        setLoading(true);
        dispatch(setActivityFormData({})); // Reset form data before fetching new activity

        const response = await handleGetActivity(`${paramKey}=${id}`, false);
        setLoading(false);
        if (response?.data.success) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Activity data fetched successfully',
              type: 'success'
            })
          );
          if (response?.data?.content?.length > 0) {
            const data = response?.data?.content[0].versionData;
            // Extract activityId from the correct location
            const formDataWithOrder = getFormDataWithOrder(data);
            dispatch(
              setActivityFormData({
                ...formDataWithOrder,
                status: data.status,
                ...(data.activityId && {activityId: data.activityId}),
                [paramKey]: id
              })
            );
          }
        } else if (response?.data) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Activity data fetched successfully',
              type: 'success'
            })
          );
          // Extract activityId from the correct location
          const formDataWithOrder = getFormDataWithOrder(response?.data);
          dispatch(
            setActivityFormData({
              ...formDataWithOrder,
              status: response?.data.status,
              activityId: response?.data.activityId,
              [paramKey]: id
            })
          );
        } else {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Failed to fetch activity data',
              type: 'error'
            })
          );
          logger.error('Failed to fetch activity data:', response?.message);
        }
      } catch (error) {
        setLoading(false);
        logger.error('Error fetching activity data:', error);
        handleApiError(error);
      }
    },
    [dispatch, getFormDataWithOrder, handleApiError]
  );

  useEffect(() => {
    if (id) getActivity(id);
  }, [getActivity, id]);

  // Use cancel handler from props if available (for AddActivityReview page)
  // Otherwise, use the hook's handler for direct usage
  const handleCancel = cancelModalHandlers?.handleCancel || hookHandleCancel;

  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const displayCurrency = useMemo(
    () => getCurrencySymbol(countryCode),
    [countryCode]
  );

  const isEditable = useMemo(
    () => isActivityReviewEditable(activityFormData.status),
    [activityFormData.status]
  );

  const disableDraft = useMemo(
    () => !isActivitySaveDraftEnabled(activityFormData.status),
    [activityFormData.status]
  );

  const isSurveyActivity =
    activityFormData?.activityType === ACTIVITY_TYPE.SURVEY;
  const isVideoActivity = isVideoActivityType(activityFormData?.activityType);
  const isFreeKickActivity = isFreeKickActivityType(
    activityFormData?.activityType
  );
  const isWheelOfFortuneActivity = isWheelOfFortuneActivityType(
    activityFormData?.activityType
  );
  const isBackgammonActivity = isBackgammonActivityType(
    activityFormData?.activityType
  );
  const isEasyOrderActivity =
    activityFormData?.activityType === ACTIVITY_TYPE.EASY_ORDER;

  const surveyUrl = normalizeUrl(activityFormData?.url);
  const hasExcludedSegments =
    Array.isArray(activityFormData?.excludedSegments) &&
    activityFormData.excludedSegments.length > 0;
  const isExcluded =
    Boolean(activityFormData?.segmentExclude) && hasExcludedSegments;

  const editButtonSchema = {
    className: 'ml-8 flex items-center text-sm text-[#0069D7] border-none',
    title: 'Edit',
    size: SIZE.SMALL,
    variant: COLOR.TERTIARY,
    isDark: true
  };

  const getCustomModal = (type) => {
    if (type === 'NAME_VALIDATION_MODAL') {
      return getModal({
        title: 'Duplicate Name Detected',
        text: (
          <>
            It looks like there's already an Activity with this name.
            <br />
            To avoid confusion, please enter a unique name before continuing.
          </>
        ),
        showPrimaryButton: true,
        primaryButtonText: 'Edit Name',
        onClickPrimaryButton: handleValidationModalClose,
        showSecondaryButton: false,
        showModal: !isNameValid,
        onCloseModal: handleValidationModalClose,
        hideCloseButton: true
      });
    }
    return null;
  };
  const renderActivityReview = () => {
    if (isSurveyActivity) {
      return (
        <SurveyActivityReview
          loading={loading}
          showToast={showToast}
          toastMessage={toastMessage}
          handleToastClose={handleToastClose}
          handleViewActivity={handleViewActivity}
          isEditable={isEditable}
          editButtonSchema={editButtonSchema}
          navigate={navigate}
          activityFormData={activityFormData}
          isExcluded={isExcluded}
          surveyUrl={surveyUrl}
          getFormattedDate={getFormattedDate}
        />
      );
    }

    if (isVideoActivity) {
      return (
        <VideoActivityReview
          loading={loading}
          showToast={showToast}
          toastMessage={toastMessage}
          handleToastClose={handleToastClose}
          handleViewActivity={handleViewActivity}
          isEditable={isEditable}
          editButtonSchema={editButtonSchema}
          navigate={navigate}
          activityFormData={activityFormData}
          isExcluded={isExcluded}
          getFormattedDate={getFormattedDate}
        />
      );
    }

    if (isFreeKickActivity) {
      return (
        <FreeKickGameReview
          loading={loading}
          showToast={showToast}
          toastMessage={toastMessage}
          handleToastClose={handleToastClose}
          handleViewActivity={handleViewActivity}
          isEditable={isEditable}
          editButtonSchema={editButtonSchema}
          navigate={navigate}
          activityFormData={activityFormData}
          isExcluded={isExcluded}
          getFormattedDate={getFormattedDate}
        />
      );
    }

    if (isBackgammonActivity) {
      return (
        <BackgammonGameReview
          loading={loading}
          showToast={showToast}
          toastMessage={toastMessage}
          handleToastClose={handleToastClose}
          handleViewActivity={handleViewActivity}
          isEditable={isEditable}
          editButtonSchema={editButtonSchema}
          navigate={navigate}
          activityFormData={activityFormData}
          isExcluded={isExcluded}
          getFormattedDate={getFormattedDate}
        />
      );
    }

    if (isWheelOfFortuneActivity) {
      return (
        <WheelOfFortuneGameReview
          loading={loading}
          showToast={showToast}
          toastMessage={toastMessage}
          handleToastClose={handleToastClose}
          handleViewActivity={handleViewActivity}
          isEditable={isEditable}
          editButtonSchema={editButtonSchema}
          navigate={navigate}
          activityFormData={activityFormData}
          isExcluded={isExcluded}
          getFormattedDate={getFormattedDate}
        />
      );
    }

    if (isEasyOrderActivity) {
      return (
        <EasyOrderActivityReview
          loading={loading}
          showToast={showToast}
          toastMessage={toastMessage}
          handleToastClose={handleToastClose}
          handleViewActivity={handleViewActivity}
          isEditable={isEditable}
          editButtonSchema={editButtonSchema}
          navigate={navigate}
          activityFormData={activityFormData}
          getFormattedDate={getFormattedDate}
        />
      );
    }

    return (
      <PageContainer data-testid='activity-review' minHeight={'65vh'}>
        {loading && <CardSkeletonLoader count={3} />}
        {!loading && (
          <>
            <div
              data-testid='activity-review-content'
              className='flex items-center justify-between'>
              <SectionTitle label={'Activity Setup'} />
              {isEditable && (
                <ButtonWithIcon
                  uiSchema={{
                    ...editButtonSchema
                  }}
                  iconLeading='edit'
                  handleButton={() => navigate(ROUTES.ACTIVITY_ADD)}
                />
              )}
            </div>
            {showToast && (
              <DuplicateAlert
                type='info'
                message={toastMessage}
                show={true}
                onCloseClick={handleToastClose}
                handleClick={handleViewActivity}
                buttonText='View Activity'
              />
            )}
            <>
              {Object.entries(activityFormData)
                .filter(([key, value]) => {
                  const excludedKeys = getExcludedKeys(
                    activityFormData?.activityType
                  );
                  return !(
                    excludedKeys.includes(key) ||
                    SURVEY_HIDDEN_KEYS.includes(key) ||
                    (value !== null &&
                      typeof value === 'object' &&
                      !Array.isArray(value)) ||
                    (Array.isArray(value) &&
                      value.length > 0 &&
                      typeof value[0] === 'object' &&
                      value[0] !== null &&
                      !Array.isArray(value[0]) &&
                      !('segmentName' in value[0]) &&
                      !('segmentId' in value[0]))
                  );
                })
                .map(([key, value]) => {
                  const defaultLabel = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase());
                  const label =
                    key === 'localCurrencyValue' && displayCurrency
                      ? `Local Currency Value (${displayCurrency})`
                      : defaultLabel;
                  return (
                    <ActivityReviewField
                      key={key}
                      label={label}
                      value={
                        key === localCurrencyText && value
                          ? `${value} ${displayCurrency}`
                          : value || ''
                      }
                    />
                  );
                })}
              {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
            </>
          </>
        )}
      </PageContainer>
    );
  };

  return (
    <ErrorBoundary>
      <>
        {renderActivityReview()}
        {!isReviewViewMode &&
          !isReviewPublished &&
          !isReviewCompleted &&
          !isReviewInProgress && (
            <SaveCancelFooter
              isReview={true}
              handleCancel={handleCancel}
              nextAction={handlePublish}
              draftAction={handleDraft}
              disableDraft={disableDraft}
            />
          )}
      </>
    </ErrorBoundary>
  );
};

ActivityReview.propTypes = {
  cancelModalHandlers: PropTypes.shape({
    handleCancel: PropTypes.func
  }),
  isReviewViewMode: PropTypes.bool
};

export default React.memo(ActivityReview);
